import { useState, useEffect, useRef } from 'react'
import FlexSearch from 'flexsearch'
import { useAppStore } from '~/lib/store'
import { useNavigate } from '@tanstack/react-router'

interface SearchResult {
    id: string
    title: string
    description?: string
    path: string
    type: 'doc' | 'task'
}

export function Search() {
    const [isOpen, setIsOpen] = useState(false)
    const [query, setQuery] = useState('')
    const [results, setResults] = useState<SearchResult[]>([])
    const [selectedIndex, setSelectedIndex] = useState(0)
    const { docTree, fetchDocTree } = useAppStore()
    const indexRef = useRef<any>(null)
    const itemsMapRef = useRef<Record<string, SearchResult>>({})
    const navigate = useNavigate()
    const inputRef = useRef<HTMLInputElement>(null)

    // Initialize search index
    useEffect(() => {
        if (!docTree) {
            fetchDocTree()
            return
        }

        try {
            // Support both FlexSearch.Index and new Index() depending on version/packaging
            const IndexConstructor = (FlexSearch as any).Index || (FlexSearch as any)
            if (typeof IndexConstructor !== 'function') {
                console.error('FlexSearch Index constructor not found', FlexSearch)
                return
            }

            const index = new IndexConstructor({
                tokenize: 'forward',
            })

            const itemsMap: Record<string, SearchResult> = {}
            let count = 0

            docTree.sections.forEach(section => {
                section.folders.forEach(node => {
                    if (node.type === 'dir') {
                        // Index folder itself
                        const folderId = `folder-${count++}`
                        const folderItem: SearchResult = {
                            id: folderId,
                            title: node.title || node.name,
                            description: node.description,
                            path: node.path,
                            type: 'doc' // Internal result type
                        }
                        itemsMap[folderId] = folderItem
                        index.add(folderId, `${folderItem.title} ${folderItem.description || ''}`)

                        // Index documents within the folder
                        node.children?.forEach(doc => {
                            const docId = `doc-${count++}`
                            const docItem: SearchResult = {
                                id: docId,
                                title: doc.title || doc.name.replace('.md', ''),
                                description: doc.description,
                                path: doc.path,
                                type: 'doc'
                            }
                            itemsMap[docId] = docItem
                            index.add(docId, `${docItem.title} ${docItem.description || ''}`)
                        })
                    } else {
                        // Index top-level file
                        const docId = `doc-${count++}`
                        const docItem: SearchResult = {
                            id: docId,
                            title: node.title || node.name.replace('.md', ''),
                            description: node.description,
                            path: node.path,
                            type: 'doc'
                        }
                        itemsMap[docId] = docItem
                        index.add(docId, `${docItem.title} ${docItem.description || ''}`)
                    }
                })
            })

            indexRef.current = index
            itemsMapRef.current = itemsMap
        } catch (err) {
            console.error('Failed to initialize search index:', err)
        }
    }, [docTree, fetchDocTree])

    // Handle search query
    useEffect(() => {
        if (!query || !indexRef.current) {
            setResults([])
            return
        }

        try {
            const foundIds = indexRef.current.search(query, 10)
            const foundResults = foundIds.map((id: any) => itemsMapRef.current[id as string]).filter(Boolean)
            setResults(foundResults)
            setSelectedIndex(0)
        } catch (err) {
            console.error('Search failed:', err)
        }
    }, [query])

    // Keyboard shortcuts
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
                e.preventDefault()
                setIsOpen(prev => !prev)
            }

            if (isOpen) {
                if (e.key === 'Escape') {
                    setIsOpen(false)
                } else if (e.key === 'ArrowDown') {
                    e.preventDefault()
                    setSelectedIndex(prev => (prev + 1) % results.length)
                } else if (e.key === 'ArrowUp') {
                    e.preventDefault()
                    setSelectedIndex(prev => (prev - 1 + results.length) % results.length)
                } else if (e.key === 'Enter' && results[selectedIndex]) {
                    e.preventDefault()
                    handleSelect(results[selectedIndex])
                }
            }
        }

        window.addEventListener('keydown', handleKeyDown)
        return () => window.removeEventListener('keydown', handleKeyDown)
    }, [isOpen, results, selectedIndex])

    useEffect(() => {
        if (isOpen) {
            setTimeout(() => inputRef.current?.focus(), 10)
        } else {
            setQuery('')
        }
    }, [isOpen])

    const handleSelect = (result: SearchResult) => {
        let targetPath = result.path

        // Only apply folder fallback if it's explicitly indexed as a folder
        if (result.id.startsWith('folder-')) {
            // Find the folder in docTree
            for (const section of docTree?.sections || []) {
                const folder = section.folders.find(f => f.path === result.path && f.type === 'dir')
                if (folder && folder.children && folder.children.length > 0) {
                    // Look for README.md first
                    const readme = folder.children.find(c => c.name.toLowerCase() === 'readme.md')
                    if (readme) {
                        targetPath = readme.path
                    } else {
                        // Fallback to first child
                        targetPath = folder.children[0].path
                    }
                    break
                }
            }
        }

        navigate({ to: '/docs/$', params: { _splat: targetPath.replace('.md', '') } })
        setIsOpen(false)
    }

    if (!isOpen) {
        return (
            <button
                onClick={() => setIsOpen(true)}
                className="flex items-center gap-2 px-3 py-1.5 bg-slate-700/50 rounded-lg text-slate-400 hover:bg-slate-700 transition-colors"
            >
                <span>🔍</span>
                <span className="text-sm">Search...</span>
                <kbd className="ml-4 text-xs bg-slate-600 px-1.5 py-0.5 rounded">⌘K</kbd>
            </button>
        )
    }

    return (
        <div className="fixed inset-0 z-[100] flex items-start justify-center pt-[15vh] px-4 pointer-events-none">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-slate-950/60 backdrop-blur-sm pointer-events-auto"
                onClick={() => setIsOpen(false)}
            />

            {/* Modal */}
            <div className="relative w-full max-w-xl bg-slate-800 border border-slate-700 rounded-xl shadow-2xl shadow-black/50 overflow-hidden pointer-events-auto animate-fade-in animate-slide-up">
                <div className="flex items-center px-4 border-b border-slate-700">
                    <span className="text-slate-400">🔍</span>
                    <input
                        ref={inputRef}
                        type="text"
                        className="w-full px-3 py-4 bg-transparent text-slate-100 placeholder-slate-500 focus:outline-none text-lg"
                        placeholder="Search documentation..."
                        value={query}
                        onChange={e => setQuery(e.target.value)}
                    />
                    <button
                        onClick={() => setIsOpen(false)}
                        className="text-xs bg-slate-700 text-slate-400 px-1.5 py-1 rounded hover:text-slate-200"
                    >
                        ESC
                    </button>
                </div>

                <div className="max-h-[60vh] overflow-y-auto">
                    {query && results.length === 0 ? (
                        <div className="p-8 text-center text-slate-400">
                            No results found for "<span className="text-slate-200">{query}</span>"
                        </div>
                    ) : results.length > 0 ? (
                        <div className="py-2">
                            {results.map((result, index) => (
                                <button
                                    key={result.id}
                                    onClick={() => handleSelect(result)}
                                    onMouseEnter={() => setSelectedIndex(index)}
                                    className={`w-full flex flex-col items-start px-4 py-3 transition-colors ${index === selectedIndex ? 'bg-blue-600/20 border-l-4 border-blue-500' : 'hover:bg-slate-700/50 border-l-4 border-transparent'
                                        }`}
                                >
                                    <div className="flex items-center gap-2">
                                        <span className="text-xs">📄</span>
                                        <span className={`font-medium ${index === selectedIndex ? 'text-blue-400' : 'text-slate-200'}`}>
                                            {result.title}
                                        </span>
                                    </div>
                                    {result.description && (
                                        <span className="text-xs text-slate-400 mt-1 line-clamp-1">
                                            {result.description}
                                        </span>
                                    )}
                                    <span className="text-[10px] text-slate-500 mt-1 uppercase tracking-wider text-left">
                                        {result.path.split('/').slice(0, -1).join(' › ')}
                                    </span>
                                </button>
                            ))}
                        </div>
                    ) : !query && (
                        <div className="p-8 text-center text-slate-500 text-sm">
                            Try searching for components, guides, or tasks...
                        </div>
                    )}
                </div>

                <div className="px-4 py-2 bg-slate-900/50 border-t border-slate-700 flex items-center justify-between text-[10px] text-slate-500 uppercase tracking-widest">
                    <div className="flex gap-4">
                        <span><kbd className="bg-slate-800 px-1 rounded border border-slate-700">↵</kbd> Select</span>
                        <span><kbd className="bg-slate-800 px-1 rounded border border-slate-700">↑↓</kbd> Navigate</span>
                    </div>
                    <span>FlexSearch Powered</span>
                </div>
            </div>
        </div>
    )
}
